import { useTranslation } from 'react-i18next';

import {
    Autocomplete,
    Box,
    FormControl,
    FormHelperText,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    ButtonComponent,
    CognitoCallbacks,
    CognitoErrorCodes,
    colors,
    CommonModal,
    GET_USERS,
    isNumber,
    OtpInput,
    TextFieldComponent,
    UPDATE_USER,
} from '@watter/shared-components';

import { confirmUserAttribute, ConfirmUserAttributeInput, updateUserAttributes } from '@aws-amplify/auth';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import Grid from '@mui/material/Grid2';
import { userEnums } from '@watter/shared-types';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { ErrorMessage, Form, Formik } from 'formik';
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { GetCountries, GetState } from 'react-country-state-city';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { useDispatch } from 'react-redux';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserImageLink, setUserName } from '../../store/reducers/userSlice';
import { MainWrapper } from '../common-styles';

import 'react-image-crop/dist/ReactCrop.css';

import { client } from '../../service/apolloClient';
import { ProfileSetPassword } from '../change-password/change-password';
import { ProfileSettingValidationSchema } from './profile-setting-validation-schema';

import lightProfile from '../../assets/images/emptyProfile.svg';

interface IFormObj {
    firstName: string;
    lastName: string;
    email: string;
    mobile_number: string;
    street: string;
    city: string;
    zipCode: string;
    profileImageURI: any;
    state?: string | null;
    phoneNumber: string;
}
interface IUser {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    profile_image: string;
    user_addresses: {
        id: number;
        street: string;
        city: string;
        zip_code: string;
        country: string;
        state: string;
    }[];
}
const ActionItems = ({
    submitForm,
    editProfile,
    t,
    setOpen,
    closeEditProfile,
    setEditProfile,
}: {
    submitForm: () => void;
    editProfile: boolean;
    t: any;
    setOpen: (open: boolean) => void;
    closeEditProfile: () => void;
    setEditProfile: (edit: boolean) => void;
}) => {
    return (
        <Grid container className="actionFooter" spacing={2} pt={2}>
            {!editProfile && (
                <Grid size={7}>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('change_password')}
                        className="cstBtn"
                        onClick={() => setOpen(true)}
                    />
                </Grid>
            )}
            {editProfile && (
                <Grid size={6}>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('cancel')}
                        className="cstBtn"
                        onClick={() => closeEditProfile()}
                    />
                </Grid>
            )}
            {editProfile && (
                <Grid size={6}>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('save')}
                        className="cstBtn"
                        type="button"
                        onClick={() => submitForm()}
                    />
                </Grid>
            )}
            {!editProfile && (
                <Grid size={5}>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('edit_profile')}
                        className="cstBtn"
                        onClick={() => setEditProfile(true)}
                    />
                </Grid>
            )}
        </Grid>
    );
};
export const ProfileSetting = () => {
    const { t } = useTranslation();
    const matches = useMediaQuery(theme => theme.breakpoints.down('md'));
    const [editProfile, setEditProfile] = useState(false);
    const [open, setOpen] = useState(false);
    const [countriesList, setCountriesList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [selectedCountryId, setCountryId] = useState(0);
    const [selectedCountryPhoneCode, setCountryPhoneCode] = useState('');
    const [selectedStateId, setStateId] = useState(0);
    const [userAddressId, setUserAddressId] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [oldPhoneNumber, setOldPhoneNumber] = useState('');
    const [state, setStateName] = useState('');
    const [country, setCountryName] = useState('');
    const [openVerifyMobilePopUp, setOpenVerifyMobilePopUp]: any = useState();
    const [isValidOtp, setIsValidOtp] = useState<boolean>(false);
    const [isResendEnabled, setIsResendEnabled] = useState<boolean>(false);
    const [otpValue, setOtpValue] = useState<string>('');
    const dispatch = useDispatch();
    const timerValueInSec = 3 * 60;
    const [timer, setTimer] = useState<number>(timerValueInSec);
    const [userCountryCode, setUserCountryCode] = useState('');
    const [userStateCode, setUserStateCode] = useState('');
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);
    const [userId, sertUserIdValue] = useState(0);
    const [formErrors, setFormErrors]: any = useState(null);
    const [profileImageUrl, setProfileImageUrl]: any = useState(null);
    /* eslint-disable no-undef */
    const [initialForm, setInitialForm] = useState<IFormObj>({
        firstName: '',
        lastName: '',
        email: '',
        mobile_number: '',
        street: '',
        city: '',
        zipCode: '',
        profileImageURI: null,
        state: '',
        phoneNumber: '',
    });

    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>({
        unit: 'px',
        width: 50,
        height: 50,
        x: 0,
        y: 0,
    });
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
    const imgRef = useRef<HTMLInputElement>(null);
    const [openModal, setOpenModal] = useState(false);
    const [initialCrop, setInitialCrop] = useState<Crop | null>(null);
    const selectImage = (file: File) => {
        if (file && file.type.startsWith('image/')) {
            setImageSrc(URL.createObjectURL(file));
            setCroppedImageUrl(null);
        } else {
            dispatch(
                openToast({
                    show: true,
                    message: t('please_select_valid_img'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        }
    };
    const compressToMaxSize = async (canvas: HTMLCanvasElement, maxSizeMB: number = 6): Promise<Blob | null> => {
        return new Promise(resolve => {
            let quality = 1.0;

            const tryCompress = () => {
                canvas.toBlob(
                    async blob => {
                        if (!blob) {
                            resolve(null);
                            return null;
                        }

                        const sizeInMB = blob.size / (1024 * 1024);
                        if (sizeInMB <= maxSizeMB || quality <= 0.1) {
                            resolve(blob);
                            return blob;
                        }

                        quality -= 0.1;
                        tryCompress(); // retry with lower quality
                        return null;
                    },
                    'image/jpeg',
                    quality,
                );
            };

            tryCompress();
        });
    };

    const cropImageNow = async () => {
        if (!crop.width || !crop.height || !image) {
            dispatch(
                openToast({
                    show: true,
                    message: t('please_select_valid_crop'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            return;
        }

        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Ensure the cropped area is based on the image's original resolution
        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Unable to get canvas context.');
            return;
        }

        // Disable image smoothing for sharpness
        ctx.imageSmoothingEnabled = false;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            crop.x! * scaleX,
            crop.y! * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height,
        );

        const blob = await compressToMaxSize(canvas, 6);
        if (blob) {
            const file = new File([blob], 'compressed-image.jpg', { type: 'image/jpeg' });
            setCroppedImageUrl(URL.createObjectURL(file));
            setInitialForm({ ...initialForm, profileImageURI: file });
        }
    };

    const fetchImageUrl = () => {
        return getUrl({
            path: initialForm.profileImageURI, // Use path instead of key
            options: {
                expiresIn: 3600,
            },
        })
            .then(result => {
                if (result && result.url) {
                    console.warn('Resolved result:', result.url);
                    return result.url;
                }
                console.error('No URL found in the result:', result);
                return null;
            })
            .catch(err => {
                console.error('Error fetching image:', err);
                return null;
            });
    };
    useEffect(() => {
        if (initialForm.profileImageURI) {
            const loadImage = async () => {
                const url = await fetchImageUrl();
                if (url) {
                    setProfileImageUrl(url);
                }
            };
            loadImage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialForm.profileImageURI]);
    /* eslint-disable no-undef */
    const [userInfo, setUserInfo] = useState<IUser>({
        id: -1,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        profile_image: '',
        user_addresses: [
            {
                id: -1,
                street: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
            },
        ],
    });
    React.useEffect(() => {
        GetCountries().then((result: any) => {
            const filteredCountries: any = [];
            result.forEach((ct: any) => {
                if (ct.name === 'India' || ct.name === 'United States') {
                    filteredCountries.push({ ...ct, phone_code: `+${ct.phone_code}` });
                }
                setCountriesList(filteredCountries);
            });
        });
    }, []);
    React.useEffect(() => {
        const data: any = countriesList.filter((i: any) => i.iso2 === userCountryCode);
        setCountryId(data[0]?.id);
        setCountryName(data[0]?.name);
        setCountryPhoneCode(data[0]?.phone_code);
        setStateList([]);
        setStateId(0);
        setInitialForm(prevForm => ({ ...prevForm, state: '' }));
        GetState(data[0]?.id).then((result: any) => {
            setStateList(result);
        });
    }, [userCountryCode, countriesList]);

    React.useEffect(() => {
        const data: any = stateList.filter((i: any) => i.state_code === userStateCode);
        setStateId(data[0]?.id);
        setInitialForm(prevForm => ({ ...prevForm, state: data[0]?.name }));
        setStateName(data[0]?.name);
    }, [stateList, userStateCode]);

    React.useEffect(() => {
        const number = phoneNumber.startsWith(selectedCountryPhoneCode)
            ? phoneNumber.slice(selectedCountryPhoneCode?.length)
            : phoneNumber;
        setPhoneNumber(number);
    }, [selectedCountryPhoneCode, phoneNumber]);

    const validatePhoneNumber = (num: string) => {
        let isValid = false;
        if (isNumber(num) && num?.length === 10) {
            isValid = true;
        }
        return isValid;
    };

    React.useEffect(() => {
        if (phoneNumber && phoneNumber !== oldPhoneNumber) {
            if (validatePhoneNumber(phoneNumber)) {
                setIsValidPhoneNumber(true);
            } else {
                setIsValidPhoneNumber(false);
            }
        } else {
            setIsValidPhoneNumber(true);
        }
    }, [phoneNumber, oldPhoneNumber]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | undefined;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1);
            }, 1000);
        } else {
            setIsResendEnabled(true);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timer]);
    const fetchUserData = async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_USERS,
                fetchPolicy: 'network-only',
            });
            if (data && data.getUser) {
                const user: IUser = data.getUser;
                const address = user.user_addresses?.[0] || {};
                setUserAddressId(address.id);
                setOldPhoneNumber(user.phone_number);
                setPhoneNumber(user.phone_number.trim());
                setUserCountryCode(address.country);
                setUserStateCode(address.state);
                sertUserIdValue(user.id);
                setInitialForm({
                    firstName: user.first_name || '',
                    lastName: user.last_name || '',
                    email: user.email || '',
                    mobile_number: user.phone_number || '',
                    street: address.street || '',
                    city: address.city || '',
                    zipCode: address.zip_code || '',
                    profileImageURI: user.profile_image || null,
                    state: address.state || '',
                    phoneNumber: user.phone_number.trim(),
                });
                setUserInfo(prevUserInfo => ({
                    ...prevUserInfo,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    email: user.email,
                    phone_number: user.phone_number.trim(),
                    profile_image: user.profile_image,
                    user_addresses: [
                        {
                            id: address.id,
                            street: address.street,
                            city: address.city,
                            zip_code: address.zip_code,
                            country: address.country,
                            state: address.state,
                        },
                    ],
                }));
            }
            dispatch(hideLoader());
        } catch (error) {
            console.error('Error fetching user data:', error);
            dispatch(hideLoader());
        }
    };

    const uploadImage = async (file: File): Promise<string> => {
        try {
            const imageName = nanoid();
            const s3Path = `${userId}/${imageName}.jpg`; // Path for the S3 object
            const result = await uploadData({
                path: s3Path,
                data: file,
                options: {
                    contentType: 'image/jpg', // Ensure correct content type
                },
            }).result;

            // If upload successful, return the file's URL from S3
            if (result?.path) {
                return result.path; // This should be the S3 URL of the uploaded image
            }
            throw new Error('Failed to upload image');
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error; // Throw error if upload fails
        }
    };

    const getCountryCode = () => {
        const countryObj: any = countriesList.filter((i: any) => i.id === selectedCountryId);
        return countryObj[0]?.iso2;
    };
    const getStateCode = () => {
        const stateObj: any = stateList.filter((i: any) => i.id === selectedStateId);
        return stateObj[0]?.state_code;
    };
    const isValidForm = () => {
        const error: any = {};
        if (!selectedCountryId) {
            error.country = t('select_country');
        }
        setFormErrors(error);
        return Object.keys(error).length === 0;
    };
    const getProfileImageURL = async (profileImageURI: any): Promise<string | null> => {
        if (profileImageURI instanceof File) {
            return uploadImage(profileImageURI);
        }
        if (typeof profileImageURI === 'string') {
            return profileImageURI || null;
        }
        return null;
    };

    const showProfileUpdateToast = (success: boolean) => {
        dispatch(
            openToast({
                show: true,
                message: success ? 'Profile updated successfully!' : 'Error updating profile. Please try again.',
                severity: success ? 'success' : 'error',
                isAlertOpen: true,
            }),
        );
    };

    const updateReduxUser = (data: any, formData: IFormObj, profileImageURL: string | null) => {
        const updatedFirstName = data?.updateUser?.first_name || formData.firstName;
        const updatedLastName = data?.updateUser?.last_name || formData.lastName;
        const updatedUserName = `${updatedFirstName} ${updatedLastName}`.trim();
        dispatch(setUserName(updatedUserName));
        if (profileImageURL) {
            dispatch(setUserImageLink(profileImageURL));
        }
    };

    const editProfileAPI = async (formData: IFormObj) => {
        if (!isValidPhoneNumber) {
            dispatch(
                openToast({ show: true, message: 'Enter valid phone number', severity: 'error', isAlertOpen: true }),
            );
            return;
        }

        try {
            dispatch(showLoader());
            const profileImageURL = await getProfileImageURL(formData.profileImageURI);

            setInitialForm({
                ...initialForm,
                mobile_number: selectedCountryPhoneCode + phoneNumber,
            });

            const input: any = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone_number: selectedCountryPhoneCode + phoneNumber,
                user_addresses: {
                    address_type: userEnums.AddressType.billing,
                    city: formData.city,
                    country: getCountryCode()?.toString(),
                    id: userAddressId,
                    state: getStateCode()?.toString(),
                    street: formData.street,
                    zip_code: formData.zipCode,
                },
            };
            if (profileImageURL) {
                input.profile_image = profileImageURL;
            }

            const { data } = await client.mutate({
                mutation: UPDATE_USER,
                variables: { input },
            });

            console.warn('Profile updated:', data);

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateUser?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            updateReduxUser(data, formData, profileImageURL);
            fetchUserData();
            setEditProfile(false);
            dispatch(hideLoader());
        } catch (err) {
            dispatch(hideLoader());
            console.error('Error saving profile:', err);
            showProfileUpdateToast(false);
        } finally {
            dispatch(hideLoader());
        }
    };
    const handleVerifyMobileNumber = async () => {
        if (isValidPhoneNumber) {
            try {
                dispatch(showLoader());
                const result: any = await updateUserAttributes({
                    userAttributes: {
                        phone_number: selectedCountryPhoneCode + phoneNumber,
                    },
                });
                console.warn(result, 'result in handleVerifyMobileNumber');
                switch (result?.phone_number?.nextStep?.updateAttributeStep) {
                    case CognitoCallbacks.DONE:
                        setOpenVerifyMobilePopUp(false);
                        editProfileAPI(initialForm);
                        break;
                    case CognitoCallbacks.CONFIRM_ATTRIBUTE_WITH_CODE:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('enter_otp_received_to_new_number'),
                                severity: 'success',
                                isAlertOpen: true,
                            }),
                        );
                        setTimer(timerValueInSec);
                        setOpenVerifyMobilePopUp(true);
                        break;
                    default:
                }
                dispatch(hideLoader());
            } catch (err: any) {
                console.error(err, 'err in verify mobile number API');
                dispatch(hideLoader());
                switch (err?.name) {
                    case CognitoErrorCodes.INVALID_PARAM_EXCEPTION:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('invalid_phone_number'),
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                    case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('profile_not_authorised_err_msg'),
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                    default:
                        dispatch(
                            openToast({
                                show: true,
                                message: err?.message,
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                }
            } finally {
                dispatch(hideLoader());
            }
        } else {
            dispatch(
                openToast({ show: true, message: 'Enter valid phone number', severity: 'error', isAlertOpen: true }),
            );
        }
    };
    const onSubmit = async (values: IFormObj) => {
        if (isValidForm()) {
            const newMobileNumber = selectedCountryPhoneCode + phoneNumber;
            if (newMobileNumber !== oldPhoneNumber) {
                handleVerifyMobileNumber();
            } else if (editProfile) {
                await editProfileAPI(values);
            }
        }
    };

    const closeEditProfile = () => {
        setEditProfile(false);
        setInitialForm({
            ...initialForm,
            firstName: userInfo.first_name,
            lastName: userInfo.last_name,
            email: userInfo.email,
            mobile_number: userInfo.phone_number,
            street: userInfo.user_addresses[0]?.street,
            city: userInfo.user_addresses[0]?.city,
            zipCode: userInfo.user_addresses[0]?.zip_code,
            profileImageURI: userInfo.profile_image,
            state: userInfo.user_addresses[0]?.state,
            phoneNumber: userInfo.phone_number,
        });
        setPhoneNumber(userInfo.phone_number.trim());
        setUserCountryCode(userInfo.user_addresses[0]?.country ?? '');
        setUserStateCode(userInfo.user_addresses[0]?.state ?? '');
        setImageSrc(profileImageUrl);
        setCroppedImageUrl(profileImageUrl);
    };

    useEffect(() => {
        fetchUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleCountryChange = (e: any) => {
        setCountryId(e?.id);
        GetState(e?.id).then((result: any) => {
            setStateList(result);
        });
        const data: any = countriesList.filter((i: any) => i.id === e?.id);
        setUserCountryCode(data[0]?.iso2);
        setCountryPhoneCode(data[0]?.phone_code); // to show country prefix
        // clearing address fields on country change
        initialForm.city = '';
        initialForm.street = '';
        initialForm.zipCode = '';
        setUserStateCode('');
        setPhoneNumber('');
        initialForm.phoneNumber = '';
    };
    const handleStateChange = (e: any) => {
        setStateId(e?.id);
        setInitialForm({ ...initialForm, state: e?.id });
        const data: any = stateList.filter((i: any) => i.id === e?.id);
        setUserStateCode(data[0]?.state_code);
    };
    const handlePhoneNumberChange = (e: any) => {
        setPhoneNumber(e.target.value);
        setInitialForm({ ...initialForm, phoneNumber: e.target.value });
    };
    const handleCloseVerifyPopUp = () => {
        setOpenVerifyMobilePopUp(false);
        closeEditProfile();
    };
    const validateOtp = async ({ userAttributeKey, confirmationCode }: ConfirmUserAttributeInput) => {
        if (isValidOtp) {
            try {
                await confirmUserAttribute({ userAttributeKey, confirmationCode });
                handleVerifyMobileNumber();
            } catch (err: any) {
                switch (err?.name) {
                    case CognitoErrorCodes.CODE_MISMATCH_EXCEPTION:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('otp_invalid_message'),
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                    case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('otp_expired_message'),
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                    case CognitoErrorCodes.SIGN_IN_EXCEPTION:
                        dispatch(
                            openToast({
                                show: true,
                                message: t('sign_in_exception_message'),
                                severity: 'error',
                                isAlertOpen: true,
                            }),
                        );
                        break;
                    default:
                        dispatch(openToast({ show: true, message: err?.name, severity: 'error', isAlertOpen: true }));
                }
            }
        } else {
            dispatch(
                openToast({
                    show: true,
                    message: t('enter_otp_received_to_new_number'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        }
    };
    const handleOtpChange = (otp: string, length: number) => {
        setOtpValue(otp);
        if (otp?.length === length) {
            setIsValidOtp(true);
        }
    };
    const resendOtp = async () => {
        setIsValidOtp(false);
        setTimer(timerValueInSec);
        setIsResendEnabled(false);
        handleVerifyMobileNumber();
    };
    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper: Render label with optional astric
    const renderLabel = (labelKey: string, showAstric = false) => (
        <Typography variant="subtitle1" mb={1} color="info.100">
            {t(labelKey)}
            {showAstric && (
                <Box component="span" color={colors?.astric}>
                    *
                </Box>
            )}
        </Typography>
    );
    // Helper: Render text field with error
    const renderTextField = ({
        name,
        value,
        placeholder,
        onChange,
        disabled = false,
        required = false,
    }: {
        name: string;
        value: any;
        placeholder?: string;
        onChange?: any;
        disabled?: boolean;
        required?: boolean;
    }) => (
        <>
            <TextFieldComponent
                variant="outlined"
                disabled={disabled}
                required={required}
                name={name}
                type="text"
                placeholder={placeholder}
                onChange={onChange}
                value={value}
            />
            <FormHelperText error>
                <ErrorMessage name={name} />
            </FormHelperText>
        </>
    );

    // Helper: Render disabled text field with label
    const renderDisabledField = (labelKey: string, name: string, value: any) => (
        <>
            {renderLabel(labelKey)}
            <TextFieldComponent variant="outlined" disabled required name={name} type="text" value={value || '-'} />
        </>
    );
    // Helper: Render Autocomplete field
    const renderAutocompleteField = ({
        options,
        getOptionLabel,
        value,
        onChange,
        label,
        error,
        name,
    }: {
        options: any[];
        getOptionLabel: (option: any) => string;
        value: any;
        onChange: (event: any, newValue: any) => void;
        label: React.ReactNode;
        error?: string;
        name: string;
    }) => (
        <>
            <FormControl className="cstSelectProfile cstState">
                <Typography variant="subtitle1" color="info.100" sx={{ height: 10 }}>
                    {label}
                </Typography>
                <Autocomplete
                    options={options}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                    value={value}
                    onChange={onChange}
                    renderInput={params => (
                        <TextField
                            name={name}
                            {...params}
                            inputProps={{ ...params.inputProps }}
                            InputLabelProps={{ sx: { display: 'none' } }}
                        />
                    )}
                />
            </FormControl>
            {error && (
                <Typography variant="subtitle1" color={`${colors.error} !important`}>
                    {error}
                </Typography>
            )}
            <FormHelperText error>
                <ErrorMessage name={name} />
            </FormHelperText>
        </>
    );
    // Helper: Render country select/autocomplete
    const renderCountryField = () => {
        if (!editProfile) return <Stack width="100%">{renderDisabledField('country', 'country', country)}</Stack>;
        return (
            <Stack width="100%">
                {renderAutocompleteField({
                    options: countriesList,
                    getOptionLabel: (option: any) => option.name || '',
                    value: selectedCountryId
                        ? countriesList.find((country: any) => country.id === selectedCountryId)
                        : '',
                    onChange: (_, newValue) => handleCountryChange(newValue),
                    label: (
                        <>
                            {t('select_country')}{' '}
                            <Box component="span" color={colors.astric}>
                                *
                            </Box>
                        </>
                    ),
                    error: formErrors?.country,
                    name: 'country',
                })}
            </Stack>
        );
    };
    // Helper: Render state select/autocomplete
    const renderStateField = () => (
        <Stack width="100%">
            {!editProfile
                ? renderDisabledField('state', 'state', state)
                : renderAutocompleteField({
                      options: stateList,
                      getOptionLabel: (option: any) => option.name || '',
                      value: selectedStateId ? stateList.find((state: any) => state.id === selectedStateId) : '',
                      onChange: (_, newValue) => handleStateChange(newValue),
                      label: (
                          <>
                              Select State{' '}
                              <Box component="span" color={colors.astric}>
                                  *
                              </Box>
                          </>
                      ),
                      name: 'state',
                  })}
        </Stack>
    );
    // Helper: Render mobile number field
    const renderMobileField = (values: any) => (
        <Stack className="cstVerify cstMobileNumber">
            {renderLabel('mobile_no', editProfile)}
            {!editProfile ? (
                <TextFieldComponent
                    variant="outlined"
                    disabled
                    required
                    name="mobile_number"
                    type="string"
                    placeholder={t('enter_mobile_number')}
                    value={values?.mobile_number}
                />
            ) : (
                <Box display="flex" gap={1}>
                    <FormControl className="cstSelectProfile" sx={{ width: '20%' }}>
                        <TextFieldComponent
                            variant="outlined"
                            type="string"
                            onChange={handlePhoneNumberChange}
                            value={selectedCountryPhoneCode}
                            disabled
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextFieldComponent
                            variant="outlined"
                            name="phoneNumber"
                            type="string"
                            placeholder={t('enter_mobile_number')}
                            onChange={handlePhoneNumberChange}
                            value={phoneNumber}
                        />
                    </FormControl>
                    <FormHelperText error>
                        <ErrorMessage name="phoneNumber" />
                    </FormHelperText>
                </Box>
            )}
            <CommonModal
                className="cstVerifyModal"
                modalOpen={openVerifyMobilePopUp}
                header={
                    <Stack rowGap={1} pb={2}>
                        <Typography variant="h5" color="secondary">
                            {t('otp_verification')}
                        </Typography>
                        <Typography variant="h6" color="info.main">
                            {t('enter_otp_received_to_new_number')}
                        </Typography>
                    </Stack>
                }
                cancelBtnName={t('cancel')}
                AcceptBtnName={t('verify')}
                closePopup={handleCloseVerifyPopUp}
                acceptBtn={() => {
                    validateOtp({
                        userAttributeKey: 'phone_number',
                        confirmationCode: otpValue,
                    });
                }}
            >
                <Stack>
                    <OtpInput
                        isValidOtp={isValidOtp}
                        showText
                        type="password"
                        autoFocus
                        length={6}
                        onChange={handleOtpChange}
                    />
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" color={colors?.gray}>
                            {t('otp_not_recieved_message')}
                        </Typography>
                        <ButtonComponent
                            className="cstResendBtn"
                            variant="text"
                            size="medium"
                            buttonName={t('otp_resend_button_title')}
                            disabled={!isResendEnabled}
                            onClick={isResendEnabled ? resendOtp : undefined}
                        />
                        <Typography variant="h6" className="timer">
                            {timer ? formatTime(timer) : ''}
                        </Typography>
                    </Stack>
                </Stack>
            </CommonModal>
            {!isValidPhoneNumber && (
                <FormHelperText error>
                    <Typography variant="subtitle1" color={`${colors.error} !important`}>
                        {t('invalid_phone_number')}
                    </Typography>
                </FormHelperText>
            )}
        </Stack>
    );

    // --- Refactored for lower cognitive complexity ---
    const handleModalClose = () => {
        setImageSrc(null);
        setImage(null);
        setOpenModal(false);
    };
    const handleModalAccept = () => {
        if (!crop.width || !crop.height || !image) {
            dispatch(
                openToast({
                    show: true,
                    message: t('please_crop_and_proceed'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            return;
        }
        cropImageNow();
        setOpenModal(false);
    };
    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const minDimension = Math.min(img.width, img.height);
        const calculatedInitialCrop: Crop = {
            unit: 'px',
            width: minDimension * 0.5,
            height: minDimension * 0.5,
            x: (img.width - minDimension * 0.5) / 2,
            y: (img.height - minDimension * 0.5) / 2,
        };
        setCrop(calculatedInitialCrop);
        setInitialCrop(calculatedInitialCrop);
        setImage(img);
    };
    const handleCropChange = (newCrop: Crop) => {
        if (!initialCrop) return;
        setCrop({
            ...newCrop,
            width: Math.max(newCrop.width, initialCrop.width),
            height: Math.max(newCrop.height, initialCrop.height),
            x: Math.min(newCrop.x, crop.x + crop.width - initialCrop.width),
            y: Math.min(newCrop.y, crop.y + crop.height - initialCrop.height),
        });
    };

    // --- Helper: Profile image rendering ---
    const getProfileImage = () => {
        if (!(croppedImageUrl || profileImageUrl) && lightProfile) {
            return (
                <Box className="emptyBox">
                    <img src={lightProfile} alt="emptyProfile" width="100%" height="100%" />
                </Box>
            );
        }
        return <img src={croppedImageUrl || profileImageUrl} alt="Profile" width="100%" height="100%" />;
    };
    // --- Helper: Edit button rendering ---
    const getEditButton = () => {
        if (!editProfile) return null;
        return (
            <Box className="cstProfileImageEdit" onClick={() => imgRef.current?.click()}>
                <IconButton>
                    <PermIdentityIcon sx={{ fontSize: 40 }} />
                </IconButton>
                <input
                    type="file"
                    accept="image/*"
                    ref={imgRef}
                    style={{ display: 'none' }}
                    onChange={e => {
                        const file = e.target.files ? e.target.files[0] : null;
                        if (file) {
                            selectImage(file);
                            setOpenModal(true);
                            e.target.value = '';
                        }
                    }}
                />
            </Box>
        );
    };
    // --- Helper: Modal crop rendering ---
    const getCropModal = () => (
        <CommonModal
            className="cstProfileModal"
            header={t('drag_image_to_adjust')}
            cancelBtnName={t('cancel')}
            AcceptBtnName={t('done')}
            modalOpen={openModal}
            closePopup={handleModalClose}
            acceptBtn={handleModalAccept}
        >
            {imageSrc && (
                <Box>
                    <ReactCrop
                        crop={crop}
                        onChange={handleCropChange}
                        aspect={1}
                        onComplete={(c: PixelCrop) => setCrop(c)}
                    >
                        <img src={imageSrc} alt="Source" onLoad={handleImageLoad} style={{ maxWidth: '100%' }} />
                    </ReactCrop>
                </Box>
            )}
        </CommonModal>
    );
    // Helper: Render profile image section
    const renderProfileImageSection = (submitForm: any) => {
        // --- Main render ---
        return (
            <Box className="cstProfileImage" justifyContent={editProfile ? 'flex-start' : 'center'}>
                <Grid sx={{ height: editProfile ? '100%' : 'auto' }} width="100%">
                    <Grid size={12} sx={{ height: 'auto' }}>
                        <Stack
                            sx={{ height: editProfile ? '100%' : 'auto' }}
                            className={
                                !(croppedImageUrl || profileImageUrl) && lightProfile
                                    ? 'cstProfileImageStack cstEmptyProfile'
                                    : 'cstProfileImageStack'
                            }
                        >
                            {getCropModal()}
                            {getProfileImage()}
                            {getEditButton()}
                        </Stack>
                    </Grid>
                    {!matches && (
                        <ActionItems
                            submitForm={submitForm}
                            editProfile={editProfile}
                            t={t}
                            setOpen={setOpen}
                            closeEditProfile={closeEditProfile}
                            setEditProfile={setEditProfile}
                        />
                    )}
                </Grid>
            </Box>
        );
        // --- End refactor ---
    };
    const renderActionsAndPassword = (submitForm: any) => (
        <>
            {matches && (
                <Grid size={12}>
                    <ActionItems
                        submitForm={submitForm}
                        editProfile={editProfile}
                        t={t}
                        setOpen={setOpen}
                        closeEditProfile={closeEditProfile}
                        setEditProfile={setEditProfile}
                    />
                </Grid>
            )}
            {open && <ProfileSetPassword open={open} closePopup={() => setOpen(false)} />}
        </>
    );

    // Helper: Render a single field row
    const renderFieldRow = (
        labelKey: string,
        fieldProps: {
            name: string;
            value: any;
            placeholder: string;
            onChange: (e: any) => void;
            disabled: boolean;
            required?: boolean;
        },
    ) => (
        <Stack>
            {renderLabel(labelKey, fieldProps.required)}
            {renderTextField(fieldProps)}
        </Stack>
    );
    return (
        <MainWrapper>
            <Formik
                initialValues={initialForm}
                validationSchema={ProfileSettingValidationSchema}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({ values, submitForm }) => {
                    return (
                        <Form style={{ gap: '2rem' }}>
                            <Box p={editProfile ? '11px 0px 10px 0px' : 0}>
                                <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        {renderFieldRow('first_name', {
                                            name: 'firstName',
                                            value: initialForm.firstName,
                                            placeholder: t('enter_first_name'),
                                            onChange: (e: any) =>
                                                setInitialForm({ ...initialForm, firstName: e.target.value }),
                                            disabled: !editProfile,
                                            required: editProfile,
                                        })}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        {renderFieldRow('last_name', {
                                            name: 'lastName',
                                            value: initialForm.lastName,
                                            placeholder: t('enter_last_name'),
                                            onChange: (e: any) =>
                                                setInitialForm({ ...initialForm, lastName: e.target.value }),
                                            disabled: !editProfile,
                                            required: editProfile,
                                        })}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack className="cstEmail">
                                            {renderLabel('email_id')}
                                            <TextFieldComponent
                                                variant="outlined"
                                                disabled
                                                required
                                                name="email"
                                                type="text"
                                                placeholder={t('enter_email')}
                                                value={initialForm.email}
                                                className={editProfile ? 'cstDisableEmail' : ''}
                                            />
                                            <FormHelperText error>
                                                <ErrorMessage name="email" />
                                            </FormHelperText>
                                        </Stack>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="flex-end">
                                        {renderCountryField()}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>{renderMobileField(values)}</Grid>
                                    <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="flex-end">
                                        {renderStateField()}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        {renderFieldRow('city', {
                                            name: 'city',
                                            value: initialForm.city,
                                            placeholder: t('enter_city'),
                                            onChange: (e: any) =>
                                                setInitialForm({ ...initialForm, city: e.target.value }),
                                            disabled: !editProfile,
                                        })}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        {renderFieldRow('building_address', {
                                            name: 'street',
                                            value: initialForm.street,
                                            placeholder: t('enter_address'),
                                            onChange: (e: any) =>
                                                setInitialForm({ ...initialForm, street: e.target.value }),
                                            disabled: !editProfile,
                                        })}
                                    </Grid>
                                    <Grid size={12}>
                                        {renderFieldRow('zip_code', {
                                            name: 'zipCode',
                                            value: initialForm.zipCode,
                                            placeholder: t('enter_zip_code'),
                                            onChange: (e: any) =>
                                                setInitialForm({ ...initialForm, zipCode: e.target.value }),
                                            disabled: !editProfile,
                                        })}
                                    </Grid>
                                    {renderActionsAndPassword(submitForm)}
                                </Grid>
                            </Box>
                            {renderProfileImageSection(submitForm)}
                        </Form>
                    );
                }}
            </Formik>
        </MainWrapper>
    );
};
